// Layout Components - Mobile-optimized with Hamburger Menu
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Truck, Calendar, BarChart3, Settings, 
  LogOut, Menu, X, Moon, Sun, Shield, ShieldCheck, ChevronDown, Bell, AlertTriangle, Clock, Gauge, Shirt, GraduationCap, Lock, Layers, Tag, Tags, BriefcaseBusiness
} from 'lucide-react';
import { cn, formatDate } from '../../lib/utils';
import { useAuthStore, useUIStore, useWacheStore } from '../../lib/store';
import { authApi, dienstApi, benachrichtigungApi, featureFlagsApi, permissionsApi } from '../../lib/api';
import { APP_VERSION } from '../../lib/version';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { toast } from 'sonner';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'] },
  { 
    path: '/mitarbeiter', 
    label: 'Mitarbeiter', 
    icon: Users, 
    roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'],
    subItems: [
      { path: '/mitarbeiter/sachgebiete', menuKey: 'mitarbeiter-sachgebiete', label: 'Sachgebiete', icon: BriefcaseBusiness, roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'] },
      { path: '/praktikanten', label: 'Praktikanten', icon: GraduationCap, permission: 'praktikanten.manage', roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'] }
    ]
  },
  { path: '/fahrzeuge', label: 'Fahrzeuge', icon: Truck, roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'] },
  { 
    path: '/einteilung', 
    label: 'Einteilung', 
    icon: Calendar, 
    roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'],
    subItems: [
      { path: '/statistik', label: 'Statistik', icon: BarChart3, roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'] },
      { path: '/einteilung/import', menuKey: 'einteilung-import', label: 'Historischer Import', icon: Layers, respectFeatureFlagForDeveloper: true, roles: ['DEVELOPER'] }
    ]
  },
  { path: '/pool-kleidung', label: 'Pool-Kleidung', icon: Shirt, roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'] },
  {
    path: '/schrank',
    label: 'Schränke',
    icon: Lock,
    roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'],
  },
  { path: '/atemschutz', label: 'Atemschutz', icon: Gauge, roles: ['MITARBEITER', 'ADMIN', 'WACHABTEILUNGSLEITUNG', 'WACHLEITUNG', 'WACHLEITUNG_VERTRETUNG', 'DEVELOPER'] },
  {
    path: '/developer',
    label: 'Developer',
    icon: Shield,
    roles: ['DEVELOPER'],
    subItems: [
      { path: '/developer/rechte', menuKey: 'developer-rechte', label: 'Rechteverwaltung', icon: ShieldCheck, respectFeatureFlagForDeveloper: true, roles: ['DEVELOPER'] },
      { path: '/developer/benachrichtigungen', menuKey: 'developer-benachrichtigungen', label: 'Benachrichtigungen', icon: Bell, respectFeatureFlagForDeveloper: true, roles: ['DEVELOPER'] },
      { path: '/developer/tags', menuKey: 'developer-tags', label: 'Tags', icon: Tags, respectFeatureFlagForDeveloper: true, roles: ['DEVELOPER'] },
    ]
  },
];

// =============================================
// Benachrichtigungs-Glocke mit TÜV-Warnungen
// =============================================

function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await benachrichtigungApi.getAll({ limit: 50 });
      const data = res.data || [];
      
      // Map backend data to frontend format
      const mapped = data.map(n => ({
        id: n.id,
        type: n.typ === 'TUV_ABGELAUFEN' ? 'expired' : n.typ === 'TUV_WARNUNG' ? 'warning' : 'info',
        title: n.titel,
        message: n.nachricht,
        date: n.erstellt_am,
        isRead: n.ist_gelesen,
        referenzId: n.referenz_id,
        priority: n.typ === 'TUV_ABGELAUFEN' ? 1 : n.typ === 'TUV_WARNUNG' ? 2 : 3
      }));
      
      // Sort: unread first, then by priority
      mapped.sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
        return a.priority - b.priority;
      });
      
      setNotifications(mapped);
    } catch (error) {
      console.error('Load notifications error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadNotifications]);
  
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const expiredCount = notifications.filter(n => n.type === 'expired' && !n.isRead).length;
  
  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.isRead) {
      try {
        await benachrichtigungApi.markAsRead(notification.id);
        loadNotifications();
      } catch (error) {
        console.error('Mark as read error:', error);
      }
    }
  };
  
  const handleMarkAllRead = async () => {
    try {
      await benachrichtigungApi.markAllAsRead();
      toast.success('Alle als gelesen markiert');
      loadNotifications();
    } catch (error) {
      console.error('Mark all read error:', error);
      toast.error('Fehler beim Markieren');
    }
  };
  
  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await benachrichtigungApi.delete(notificationId);
      loadNotifications();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className={cn(
              'absolute -top-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white',
              expiredCount > 0 ? 'bg-red-500' : 'bg-amber-500'
            )}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Benachrichtigungen
            {unreadCount > 0 && (
              <Badge variant="secondary">{unreadCount}</Badge>
            )}
          </h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleMarkAllRead}>
              Alle gelesen
            </Button>
          )}
        </div>
        
        <ScrollArea className="max-h-[350px]">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Wird geladen...
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground">Keine Benachrichtigungen</p>
              <p className="text-xs text-muted-foreground mt-1">Alle TÜV-Termine sind aktuell</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={cn(
                    'w-full p-3 text-left hover:bg-muted/50 transition-colors flex items-start gap-3 group',
                    n.isRead ? 'opacity-60 bg-muted/20' : '',
                    !n.isRead && n.type === 'expired' ? 'bg-red-50/50' : '',
                    !n.isRead && n.type === 'warning' ? 'bg-amber-50/50' : ''
                  )}
                >
                  <div className={cn(
                    'mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0',
                    n.type === 'expired' ? 'bg-red-100' : n.type === 'warning' ? 'bg-amber-100' : 'bg-slate-100'
                  )}>
                    {n.type === 'expired' ? (
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    ) : n.type === 'warning' ? (
                      <Clock className="w-4 h-4 text-amber-600" />
                    ) : (
                      <Bell className="w-4 h-4 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <p className={cn(
                        'text-sm font-medium leading-tight',
                        n.isRead ? 'text-muted-foreground' : 
                        n.type === 'expired' ? 'text-red-700' : 
                        n.type === 'warning' ? 'text-amber-700' : ''
                      )}>
                        {n.title}
                      </p>
                      {!n.isRead && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap break-words">{n.message}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(n.date, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <button 
                        onClick={(e) => handleDeleteNotification(e, n.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 rounded transition-opacity"
                      >
                        <X className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

// Desktop Sidebar - hidden on mobile
export function Sidebar() {
  const location = useLocation();
  const { user, hasEffectivePermission } = useAuthStore();
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { wache } = useWacheStore();
  const [activeMenuKeys, setActiveMenuKeys] = useState([]);

  // Load active menu keys from Feature Flags
  useEffect(() => {
    const loadActiveMenuKeys = async () => {
      try {
        const response = await featureFlagsApi.getActiveMenuKeys();
        setActiveMenuKeys(response.data || []);
      } catch (error) {
        console.error('Failed to load feature flags:', error);
        // Fallback: all menus active
        setActiveMenuKeys(navItems.map(item => item.path.replace('/', '')));
      }
    };
    loadActiveMenuKeys();
  }, []);

  const filterByFlags = (item) => {
    if (!item.roles.includes(user?.rolle)) return false;
    if (item.permission && !hasEffectivePermission(item.permission)) return false;
    if (item.alwaysVisible) return true;
    if (user?.rolle === 'DEVELOPER' && !item.respectFeatureFlagForDeveloper) return true;
    const menuKey = item.menuKey || item.path.replace(/^\//, '').replace(/\//g, '-');
    return activeMenuKeys.includes(menuKey);
  };

  const filteredNavItems = navItems
    .filter(filterByFlags)
    .map(item => ({
      ...item,
      subItems: (item.subItems || []).filter(filterByFlags),
    }));

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-card border-r transition-all duration-300',
        'hidden lg:flex flex-col overflow-hidden', // Hide on mobile, show on lg+
        sidebarOpen ? 'w-64' : 'w-16'
      )}
    >
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center justify-between px-4 border-b">
        {sidebarOpen && (
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <img
                src={wache?.logo_url || '/logo-fwonline.svg'}
                alt="Wache-Logo"
                className="w-full h-full object-contain"
                onError={(e) => { e.target.src = '/logo-fwonline.svg'; }}
              />
            </div>
            <div>
              <h1 className="font-heading font-bold text-sm">FW {wache?.nummer || '...'}</h1>
              <p className="text-xs text-muted-foreground truncate max-w-[140px]">
                {wache?.name || 'Digitale Feuerwache'}
              </p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="shrink-0"
          data-testid="sidebar-toggle"
        >
          {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2 space-y-1">
        {filteredNavItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path) && !item.subItems?.some(s => location.pathname.startsWith(s.path)));
          const Icon = item.icon;
          const hasSubItems = item.subItems && item.subItems.length > 0;
          
          return (
            <div key={item.path}>
              <Link
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors',
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {sidebarOpen && <span className="font-medium flex-1">{item.label}</span>}
              </Link>
              {/* Sub-Items (only when sidebar open) */}
              {sidebarOpen && hasSubItems && (
                <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                  {item.subItems.map((sub) => {
                    const SubIcon = sub.icon;
                    const subActive = location.pathname === sub.path ||
                      location.pathname.startsWith(sub.path);
                    return (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        data-testid={`nav-sub-${sub.path.slice(1)}`}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 rounded-md transition-colors text-sm',
                          subActive
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )}
                      >
                        <SubIcon className="w-4 h-4 shrink-0" />
                        <span>{sub.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

// Mobile Navigation Sheet
function MobileNav({ open, onOpenChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, clearUser, hasEffectivePermission } = useAuthStore();
  const { wache, clear: clearWache } = useWacheStore();
  const [activeMenuKeys, setActiveMenuKeys] = useState([]);

  // Load active menu keys
  useEffect(() => {
    const loadActiveMenuKeys = async () => {
      try {
        const response = await featureFlagsApi.getActiveMenuKeys();
        setActiveMenuKeys(response.data || []);
      } catch (error) {
        console.error('Failed to load feature flags:', error);
        setActiveMenuKeys(navItems.map(item => item.path.replace('/', '')));
      }
    };
    loadActiveMenuKeys();
  }, []);

  const filterByFlags = (item) => {
    if (!item.roles.includes(user?.rolle)) return false;
    if (item.permission && !hasEffectivePermission(item.permission)) return false;
    if (item.alwaysVisible) return true;
    if (user?.rolle === 'DEVELOPER' && !item.respectFeatureFlagForDeveloper) return true;
    const menuKey = item.menuKey || item.path.replace(/^\//, '').replace(/\//g, '-');
    return activeMenuKeys.includes(menuKey);
  };

  const filteredNavItems = navItems
    .filter(filterByFlags)
    .map(item => ({
      ...item,
      subItems: (item.subItems || []).filter(filterByFlags),
    }));

  const handleLogout = async () => {
    try {
      await authApi.logout();
      clearUser();
      clearWache();
      toast.success('Erfolgreich abgemeldet');
      navigate('/login');
    } catch (error) {
      clearUser();
      clearWache();
      navigate('/login');
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-72 p-0 flex flex-col overflow-hidden !gap-0">
        <SheetHeader className="shrink-0 p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <SheetTitle className="text-left">FW {wache?.nummer || '...'}</SheetTitle>
              <p className="text-sm text-muted-foreground">
                {wache?.name || 'Digitale Feuerwache'}
              </p>
            </div>
          </div>
        </SheetHeader>

        {/* User Info */}
        <div className="shrink-0 p-4 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-primary text-primary-foreground">
                {user?.vorname?.[0]}{user?.nachname?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{user?.vorname} {user?.nachname}</p>
              <p className="text-sm text-muted-foreground">{user?.rolle}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            
            return (
              <div key={item.path}>
                <button
                  onClick={() => handleNavigate(item.path)}
                  data-testid={`mobile-nav-${item.path.slice(1)}`}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left transition-colors',
                    isActive 
                      ? 'bg-primary text-primary-foreground' 
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
                {hasSubItems && (
                  <div className="ml-6 pl-3 border-l border-border mt-1 mb-1 space-y-1">
                    {item.subItems.map((sub) => {
                      const SubIcon = sub.icon;
                      const subActive = location.pathname === sub.path ||
                        location.pathname.startsWith(sub.path);
                      return (
                        <button
                          key={sub.path}
                          onClick={() => handleNavigate(sub.path)}
                          data-testid={`mobile-nav-sub-${sub.path.slice(1)}`}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left text-sm transition-colors',
                            subActive
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          )}
                        >
                          <SubIcon className="w-4 h-4" />
                          <span>{sub.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="shrink-0 p-2 border-t">
          <button
            onClick={() => handleNavigate('/profil')}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Users className="w-5 h-5" />
            <span className="font-medium">Profil</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Abmelden</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function Header() {
  const navigate = useNavigate();
  const { user, clearUser } = useAuthStore();
  const { theme, toggleTheme, sidebarOpen } = useUIStore();
  const { wache, clear: clearWache } = useWacheStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  const location = useLocation();
  const pathname = location.pathname;
  useEffect(() => {
    // Using a ref pattern to avoid the lint warning
    const timer = setTimeout(() => setMobileMenuOpen(false), 0);
    return () => clearTimeout(timer);
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
      clearUser();
      clearWache();
      toast.success('Erfolgreich abgemeldet');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearUser();
      clearWache();
      navigate('/login');
    }
  };

  const getInitials = () => {
    if (!user) return '??';
    return `${user.vorname?.[0] || ''}${user.nachname?.[0] || ''}`.toUpperCase();
  };

  return (
    <>
      <header 
        className={cn(
          'fixed top-0 z-50 h-16 bg-card/95 backdrop-blur border-b transition-all duration-300',
          'left-0 right-0', // Full width on mobile
          'lg:left-64', // Offset on desktop when sidebar open
          !sidebarOpen && 'lg:left-16'
        )}
      >
        <div className="h-full px-4 lg:px-6 flex items-center justify-between">
          {/* Left side */}
          <div className="flex items-center gap-3">
            {/* Mobile Hamburger Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>

            {/* Mobile Logo */}
            <div className="flex items-center gap-2 lg:hidden">
              <div className="w-8 h-8 rounded-md flex items-center justify-center overflow-hidden bg-background">
                {wache?.logo_url ? (
                  <img
                    src={wache.logo_url}
                    alt="Wache-Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-primary'); }}
                  />
                ) : (
                  <div className="w-full h-full bg-primary flex items-center justify-center">
                    <Truck className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
              </div>
              <span className="font-heading font-bold text-sm">FW {wache?.nummer || '...'}</span>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="hidden sm:flex"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </Button>

            {/* Notifications */}
            <NotificationBell />

            {/* User menu - Desktop */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="hidden sm:flex items-center gap-2 px-2"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden md:block">
                    <p className="text-sm font-medium">{user?.vorname} {user?.nachname}</p>
                    <p className="text-xs text-muted-foreground">{user?.rolle}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mein Konto</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-link">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profil')} data-testid="profile-link">
                  <Users className="w-4 h-4 mr-2" />
                  Profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Avatar - Mobile (just avatar, menu is in hamburger) */}
            <Avatar className="w-8 h-8 sm:hidden">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Sheet */}
      <MobileNav open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} />
    </>
  );
}

// App Version

export function MainLayout({ children }) {
  const { sidebarOpen } = useUIStore();
  const { user, setEffectivePermissions } = useAuthStore();

  useEffect(() => {
    if (!user) return;
    permissionsApi.getEffective()
      .then((response) => setEffectivePermissions(response.data?.permissions || []))
      .catch(() => setEffectivePermissions([]));
  }, [user?.id, setEffectivePermissions]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Sidebar />
      <Header />
      <main 
        className={cn(
          'pt-16 flex-1 transition-all duration-300',
          'pl-0 lg:pl-64', // No padding on mobile, sidebar padding on desktop
          !sidebarOpen && 'lg:pl-16'
        )}
      >
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
      {/* Footer */}
      <footer 
        className={cn(
          'py-2 px-4 text-center text-xs text-muted-foreground border-t bg-card/50 transition-all duration-300',
          'pl-0 lg:pl-64',
          !sidebarOpen && 'lg:pl-16'
        )}
      >
        © {new Date().getFullYear()} Creativey Media · Version {APP_VERSION}
      </footer>
    </div>
  );
}

export default MainLayout;
